import React, { useMemo, useCallback } from "react"
import { Bar } from "@visx/shape"
import { useTooltip } from "@visx/tooltip"
import { localPoint } from "@visx/event"
import { ParentSize } from "@visx/responsive"
import type { CategoricalDataPoint, ChartMargin } from "../types"
import { CHART_COLORS, DEFAULT_MARGIN_CATEGORICAL } from "../theme"
import { makeBandScale, makeLinearScale } from "../utils/scales"
import { ChartTooltip } from "../components/ChartTooltip"
import { StandardAxisLeft, StandardAxisBottom } from "../components/ChartAxes"

export type PilotTrackingPlatformTimeChartProps = {
    width: number
    height: number
    data: CategoricalDataPoint[]
    margin?: ChartMargin
    barOpacity?: number
}

const getLabel = (d: CategoricalDataPoint) => d.label
const getValue = (d: CategoricalDataPoint) => d.value

const InnerBarChart = ({
    width,
    height,
    data,
    margin = DEFAULT_MARGIN_CATEGORICAL,
    barOpacity = 1,
}: PilotTrackingPlatformTimeChartProps) => {
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipLeft = 0,
        tooltipTop = 0,
    } = useTooltip<CategoricalDataPoint>()

    const xScale = useMemo(
        () => makeBandScale({ domain: data.map(getLabel), rangeStart: 0, rangeEnd: innerWidth }),
        [innerWidth, data]
    )

    const yScale = useMemo(
        () =>
            makeLinearScale({
                data,
                accessor: getValue,
                rangeStart: innerHeight,
                rangeEnd: 0,
                minValue: 6,
            }),
        [innerHeight, data]
    )

    const handleMouseOver = useCallback(
        (event: React.MouseEvent<SVGRectElement>, datum: CategoricalDataPoint) => {
            const coords = localPoint(event) || { x: 0, y: 0 }
            showTooltip({ tooltipData: datum, tooltipLeft: coords.x, tooltipTop: coords.y })
        },
        [showTooltip]
    )

    return (
        <div style={{ position: "relative" }}>
            <svg width={width} height={height}>
                <rect width={width} height={height} fill={CHART_COLORS.background} rx={24} />

                <g transform={`translate(${margin.left},${margin.top})`}>
                    <StandardAxisLeft
                        scale={yScale}
                        numTicks={6}
                        labelColor={CHART_COLORS.labelLight}
                    />

                    {data.map((d, i) => {
                        const barWidth = xScale.bandwidth()
                        const barHeight = innerHeight - yScale(getValue(d))
                        const barX = xScale(getLabel(d))
                        const barY = innerHeight - barHeight

                        return (
                            <Bar
                                key={`bar-${d.label}-${barHeight}`}
                                x={barX}
                                y={barY}
                                width={barWidth}
                                height={barHeight}
                                fill={CHART_COLORS.accentDark}
                                opacity={barOpacity}
                                rx={4}
                                onMouseMove={(e) => handleMouseOver(e, d)}
                                onMouseLeave={() => hideTooltip()}
                                className="motion-safe:animate-bar-grow  origin-bottom [transform-box:fill-box] transition-opacity duration-400"
                                style={{ animationDelay: `${i * 10}ms` }}
                            />
                        )
                    })}

                    <StandardAxisBottom
                        top={innerHeight}
                        scale={xScale}
                        labelColor={CHART_COLORS.labelLight}
                    />
                </g>
            </svg>

            {tooltipData && (
                <ChartTooltip top={tooltipTop} left={tooltipLeft}>
                    <div className="font-medium">
                        {tooltipData.value.toFixed(2)} {tooltipData.value === 1 ? "hour" : "hours"}
                    </div>
                </ChartTooltip>
            )}
        </div>
    )
}

export const PilotTrackingPlatformTimeChart = ({
    data,
    height = 300,
    barOpacity,
}: {
    data: CategoricalDataPoint[]
    height?: number
    barOpacity?: number
}) => (
    <div style={{ width: "100%", height }}>
        <ParentSize>
            {({ width, height: ph }) => (
                <InnerBarChart width={width} height={ph} data={data} barOpacity={barOpacity} />
            )}
        </ParentSize>
    </div>
)
